import React from 'react';
import { Box, Typography, Alert, Tooltip, IconButton } from '@mui/material';
import { HelpOutline as HelpIcon } from '@mui/icons-material';

import { PresetAction } from '../../types';

import MessageActionEditor from './editors/MessageActionEditor';
import ChoiceActionEditor from './editors/ChoiceActionEditor/index';
import MultipleChoiceActionEditor from './editors/MultipleChoiceActionEditor';
import TemplateInputActionEditor from './editors/TemplateInputActionEditor';
import LocationInputActionEditor from './editors/LocationInputActionEditor';
import CallActionEditor from './editors/CallActionEditor/index';
import SubPresetActionEditor from './editors/SubPresetActionEditor';

interface ActionEditorProps {
  action: PresetAction;
  onChange: (action: PresetAction) => void;
  contextKeys: string[];
}

const ActionEditor: React.FC<ActionEditorProps> = ({ action, onChange, contextKeys }) => {
  const renderEditor = () => {
    switch (action.type) {
      case 'message':
        return <MessageActionEditor action={action} onChange={onChange} />;

      case 'choice':
        return <ChoiceActionEditor action={action} onChange={onChange} contextKeys={contextKeys} />;

      case 'multipleChoice':
        return (
          <MultipleChoiceActionEditor
            action={action}
            onChange={onChange}
            contextKeys={contextKeys}
          />
        );

      case 'templateInput':
        return <TemplateInputActionEditor action={action} onChange={onChange} />;

      case 'locationInput':
        return <LocationInputActionEditor action={action} onChange={onChange} />;

      case 'call':
        return <CallActionEditor action={action} onChange={onChange} contextKeys={contextKeys} />;

      case 'subPreset':
        return <SubPresetActionEditor action={action} onChange={onChange} />;

      default:
        return (
          <Alert severity="error">Unknown action type: {(action as { type: string }).type}</Alert>
        );
    }
  };

  return (
    <Box>
      {/* Context Variables Help */}
      {contextKeys.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2">
              Available context variables: {contextKeys.map((key) => `{{${key}}}`).join(', ')}
            </Typography>
            <Tooltip title="You can use these variables in text fields to insert dynamic content">
              <IconButton size="small">
                <HelpIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Alert>
      )}

      {renderEditor()}
    </Box>
  );
};

export default ActionEditor;
